import React, { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';
import { pushNotificationService } from '../services/pushNotificationService';
import { isHelpdeskRoute } from '../lib/helpdesk-utils';

type SyncStatus = 'idle' | 'syncing' | 'synced' | 'error';

interface PushNotificationSyncContextType {
  syncStatus: SyncStatus;
}

const PushNotificationSyncContext = createContext<PushNotificationSyncContextType | undefined>(undefined);

interface PushNotificationSyncProviderProps {
  children: ReactNode;
}

/**
 * Provider that automatically syncs push notification subscriptions after authentication.
 *
 * This solves the issue where push notifications stop working after token expiry and
 * re-authentication. Without this provider, users would need to visit the profile settings
 * page to trigger the push subscription sync.
 *
 * Scope: Helpdesk portal only (staff users).
 */
export const PushNotificationSyncProvider: React.FC<PushNotificationSyncProviderProps> = ({ children }) => {
  const { isAuthenticated, token, user } = useAuth();
  const syncAttemptedRef = useRef<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');

  useEffect(() => {
    const syncPushSubscription = async () => {
      // Only sync on helpdesk routes (staff users), not customer portal
      const currentPath = window.location.pathname;
      if (!isHelpdeskRoute(currentPath)) {
        return;
      }

      // Skip if not authenticated or no token
      if (!isAuthenticated || !token || !user) {
        syncAttemptedRef.current = null;
        setSyncStatus('idle');
        return;
      }

      // Create a unique key for this auth session to prevent duplicate syncs
      const authKey = `${user.id}-${token.slice(-10)}`;

      // Skip if already synced for this session
      if (syncAttemptedRef.current === authKey) {
        return;
      }

      // Check if push notifications are supported
      if (!pushNotificationService.isSupported()) {
        return;
      }

      // Check permission state - only sync if permission was previously granted
      const permissionState = pushNotificationService.getPermissionState();
      if (permissionState !== 'granted') {
        // Don't sync if denied or default - respect user's choice
        return;
      }

      try {
        setSyncStatus('syncing');
        syncAttemptedRef.current = authKey;

        // Check subscription state - this now uses device-specific localStorage tracking
        // to determine if re-subscription is needed (prevents cross-device auto-enable bug)
        const state = await pushNotificationService.getSubscriptionState();

        if (state.needsResubscription) {
          // Only auto-resubscribe if THIS device previously had push enabled
          // (localStorage endpoint exists and matches a backend subscription)
          console.log('[PushNotificationSync] Re-subscribing after auth change (same device)');
          await pushNotificationService.subscribe(navigator.platform || 'Unknown Device');
          console.log('[PushNotificationSync] Re-subscription successful');
        }

        setSyncStatus('synced');
      } catch (error) {
        console.error('[PushNotificationSync] Error syncing subscription:', error);
        setSyncStatus('error');
      }
    };

    syncPushSubscription();
  }, [isAuthenticated, token, user]);

  // Listen for route changes to sync when navigating to helpdesk routes
  useEffect(() => {
    const handleLocationChange = () => {
      const currentPath = window.location.pathname;

      // If navigating to a helpdesk route and authenticated, trigger sync check
      if (isHelpdeskRoute(currentPath) && isAuthenticated && token && user) {
        const authKey = `${user.id}-${token.slice(-10)}`;

        // Only sync if we haven't already synced for this session
        if (syncAttemptedRef.current !== authKey) {
          // Check permission and validate subscription state
          if (pushNotificationService.isSupported() &&
              pushNotificationService.getPermissionState() === 'granted') {
            syncAttemptedRef.current = authKey;
            pushNotificationService.getSubscriptionState()
              .then(async (state) => {
                if (state.needsResubscription) {
                  // Only auto-resubscribe if THIS device previously had push enabled
                  console.log('[PushNotificationSync] Re-subscribing after route change (same device)');
                  await pushNotificationService.subscribe(navigator.platform || 'Unknown Device');
                }
                setSyncStatus('synced');
              })
              .catch(error => {
                console.error('[PushNotificationSync] Error syncing subscription on route change:', error);
                setSyncStatus('error');
              });
          }
        }
      }
    };

    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', handleLocationChange);

    // Listen for pushstate/replacestate (programmatic navigation)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function(...args: Parameters<typeof originalPushState>) {
      originalPushState.apply(history, args);
      setTimeout(handleLocationChange, 0);
    };

    history.replaceState = function(...args: Parameters<typeof originalReplaceState>) {
      originalReplaceState.apply(history, args);
      setTimeout(handleLocationChange, 0);
    };

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, [isAuthenticated, token, user]);

  const contextValue: PushNotificationSyncContextType = {
    syncStatus,
  };

  return (
    <PushNotificationSyncContext.Provider value={contextValue}>
      {children}
    </PushNotificationSyncContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const usePushNotificationSync = (): PushNotificationSyncContextType => {
  const context = useContext(PushNotificationSyncContext);
  if (context === undefined) {
    throw new Error('usePushNotificationSync must be used within a PushNotificationSyncProvider');
  }
  return context;
};
